import { useState, useEffect } from 'react';
import { useCurrency } from '../../../hooks/useCurrency';
import { Link } from 'react-router-dom';
import { Calendar, DollarSign, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface SubscriptionData {
  id: string;
  product_name: string;
  billing_cycle: 'monthly' | 'quarterly' | 'yearly';
  fee: number;
  status: 'active' | 'expired' | 'trial';
  start_date: string;
  expiration_date: string;
  auto_renew: boolean;
}

interface PendingPayment {
  id: string;
  amount: number;
  total_amount: number;
  billing_cycle: string;
  reference_code: string;
  status: string;
  created_at: string;
}

export default function Subscription() {
  const { formatCurrency } = useCurrency();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [pendingPayments, setPendingPayments] = useState<PendingPayment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const { data: subData, error: subError } = await supabase
        .from('software_subscription_settings')
        .select('*')
        .order('updated_at', { ascending: false, nullsFirst: false })
        .limit(1)
        .maybeSingle();

      if (subError) {
        console.error('Subscription query error:', subError);
        alert(`Failed to load subscription: ${subError.message || 'Unknown error'}`);
        return;
      }
      setSubscription(subData);

      const { data: paymentsData, error: paymentsError } = await supabase
        .from('software_payments')
        .select('*')
        .eq('type', 'subscription')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (paymentsError) {
        console.error('Payments query error:', paymentsError);
        alert(`Failed to load pending payments: ${paymentsError.message || 'Unknown error'}`);
        return;
      }
      setPendingPayments(paymentsData || []);
    } catch (error: any) {
      console.error('Failed to load subscription data:', error);
      alert(`Failed to load subscription data: ${error?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const approvePayment = async (paymentId: string, billingCycle: string) => {
    try {
      const { error: paymentError } = await supabase
        .from('software_payments')
        .update({
          status: 'approved',
          approved_at: new Date().toISOString(),
        })
        .eq('id', paymentId);

      if (paymentError) throw paymentError;

      if (subscription) {
        const currentExpiration = new Date(subscription.expiration_date);
        let newExpiration = new Date(currentExpiration);

        switch (billingCycle) {
          case 'monthly':
            newExpiration.setMonth(newExpiration.getMonth() + 1);
            break;
          case 'quarterly':
            newExpiration.setMonth(newExpiration.getMonth() + 3);
            break;
          case 'yearly':
            newExpiration.setFullYear(newExpiration.getFullYear() + 1);
            break;
        }

        const { error: subError } = await supabase
          .from('software_subscription_settings')
          .update({
            expiration_date: newExpiration.toISOString().split('T')[0],
            status: 'active',
          })
          .eq('id', subscription.id);

        if (subError) throw subError;
      }

      alert('Payment approved successfully!');
      loadData();
    } catch (error) {
      console.error('Failed to approve payment:', error);
      alert('Failed to approve payment');
    }
  };

  const rejectPayment = async (paymentId: string) => {
    try {
      const { error } = await supabase
        .from('software_payments')
        .update({ status: 'rejected' })
        .eq('id', paymentId);

      if (error) throw error;

      alert('Payment rejected');
      loadData();
    } catch (error) {
      console.error('Failed to reject payment:', error);
      alert('Failed to reject payment');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'expired':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'trial':
        return <Clock className="h-5 w-5 text-amber-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      trial: 'bg-amber-100 text-amber-800',
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors[status as keyof typeof colors]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getDaysRemaining = (expirationDate: string) => {
    const today = new Date();
    const expiration = new Date(expirationDate);
    const diffTime = expiration.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading subscription data...</div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No subscription found</div>
      </div>
    );
  }

  const daysRemaining = getDaysRemaining(subscription.expiration_date);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Software Subscription</h1>
        <Link
          to="/softwarebilling/paymentnow"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Renew Subscription
        </Link>
      </div>

      {/* Subscription Card */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {subscription.product_name}
            </h2>
            <div className="flex items-center gap-2">
              {getStatusIcon(subscription.status)}
              {getStatusBadge(subscription.status)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-primary-600">
              {formatCurrency(subscription.fee)}
            </div>
            <div className="text-sm text-gray-500 capitalize">
              {subscription.billing_cycle}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-gray-200">
          <div>
            <div className="flex items-center text-sm text-gray-500 mb-1">
              <Calendar className="h-4 w-4 mr-2" />
              Start Date
            </div>
            <div className="text-lg font-medium text-gray-900">
              {new Date(subscription.start_date).toLocaleDateString()}
            </div>
          </div>

          <div>
            <div className="flex items-center text-sm text-gray-500 mb-1">
              <Calendar className="h-4 w-4 mr-2" />
              Expiration Date
            </div>
            <div className="text-lg font-medium text-gray-900">
              {new Date(subscription.expiration_date).toLocaleDateString()}
            </div>
          </div>

          <div>
            <div className="flex items-center text-sm text-gray-500 mb-1">
              <Clock className="h-4 w-4 mr-2" />
              Days Remaining
            </div>
            <div className={`text-lg font-medium ${daysRemaining < 7 ? 'text-red-600' : 'text-gray-900'}`}>
              {daysRemaining > 0 ? `${daysRemaining} days` : 'Expired'}
            </div>
          </div>
        </div>

        {daysRemaining < 7 && daysRemaining > 0 && (
          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center text-amber-800">
              <Clock className="h-5 w-5 mr-2" />
              <span className="font-medium">
                Your subscription will expire in {daysRemaining} days. Please renew to continue using the service.
              </span>
            </div>
          </div>
        )}

        {daysRemaining <= 0 && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center text-red-800">
              <XCircle className="h-5 w-5 mr-2" />
              <span className="font-medium">
                Your subscription has expired. Please renew immediately to continue using the service.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Pending Payments Section (Admin Only) */}
      {pendingPayments.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Pending Payment Approvals</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Billing Cycle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingPayments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {payment.reference_code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                      {payment.billing_cycle}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(payment.total_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(payment.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => approvePayment(payment.id, payment.billing_cycle)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => rejectPayment(payment.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
